import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { BOARD_VIEW_MODES, ViewMode } from "../utils/jobsBoardState";
import FlexRow from "../../../../components/base/FlexRow";
import Icon from "../../../../components/base/Icon";
import Select from "../../../../components/form/Select";
import { sortJobStatuses, useEnsureJobColors, useHasMaskedJobCalls, useJobColors, useJobMetros, useJobs, useStatuses } from "../../../../core/Global";
import Avatar from "../../../../components/base/Avatar";
import DataItem from "../../../../models/DataItem";
import Job from "../models/Job";
import { CalendarDateBasis } from "../../../../components/calendar/JobsCalendar";
import { buildJobFilter, cleanJobFilters, countJobFilters, CUSTOM, formatRange, JOB_FILTERS, JobFilterContext, JobFilterDef, JobFilterOption, jobFilterParams, JobFilterValues, rangeKey, readJobFilters, readRange, writeRange } from "../utils/jobFilters";
import DatePicker from "../../../../components/form/DatePicker";
import moment from "moment";
import "./JobsFilterBar.css";
import JobsBoardSettingsDrawer from "./JobsBoardSettingsDrawer";


export type FilterMode = "list" | "calendar" | "map"

interface JobsFilterBarProps {
    mode: FilterMode

    onGroupByChange?: (id: string) => void
    onViewByChange?: (id: CalendarDateBasis) => void

    /** The applied filters as one predicate; undefined = no filter. */
    onFilterChange?: (filter: ((job: Job) => boolean) | undefined) => void

    /** The Find text (undefined when empty); match it with jobMatchesKeyword. */
    onKeywordChange?: (keyword: string | undefined) => void
}


const GROUP_BY_OPTIONS: DataItem[] = [
    { id: "status", name: "Status", icon: "bars-progress" },
    { id: "type", name: "Type", icon: "wrench" },
    { id: "source", name: "Source", icon: "bullhorn" },
    { id: "metro", name: "Metro", icon: "map" },
    { id: "tech", name: "Technician", icon: "user-helmet-safety" },
]

const VIEW_BY_OPTIONS: { id: CalendarDateBasis; name: string; icon: string }[] = [
    { id: "appointment", name: "Appointment Date", icon: "calendar-clock" },
    { id: "created", name: "Created Date", icon: "calendar-plus" },
    { id: "lastStatus", name: "Last Status Update", icon: "clock-rotate-left" },
]

// The panel's columns, each a stack of sections (see JOB_FILTERS' `section`).
const PANEL_COLUMNS: string[][] = [["Job"], ["People"], ["Labels", "Activity"], ["Dates"]]


/** Closes a portaled popup on outside mousedown, Escape, resize and (optionally) scroll. */
const usePopupClose = (open: boolean, close: () => void, refs: React.RefObject<HTMLElement>[], onScroll: boolean) => {
    useEffect(() => {
        if (!open) return
        // The date-range picker's calendar is appended to <body>; clicks in it are "inside" too.
        const inside = (t: EventTarget | null) => refs.some(r => r.current?.contains(t as Node))
            || !!(t as Element | null)?.closest?.(".daterangepicker")
        const down = (e: MouseEvent) => { if (!inside(e.target)) close() }
        const esc = (e: KeyboardEvent) => { if (e.key == "Escape") close() }
        const scroll = (e: Event) => { if (!inside(e.target)) close() }
        document.addEventListener("mousedown", down)
        document.addEventListener("keydown", esc)
        window.addEventListener("resize", close)
        if (onScroll) window.addEventListener("scroll", scroll, true)
        return () => {
            document.removeEventListener("mousedown", down)
            document.removeEventListener("keydown", esc)
            window.removeEventListener("resize", close)
            if (onScroll) window.removeEventListener("scroll", scroll, true)
        }
    }, [open])
}


export default function JobsFilterBar(props: JobsFilterBarProps) {

    const statuses = useStatuses()
    const jobsBank = useJobs()
    const jobColors = useJobColors()
    const metros = useJobMetros()
    const maskedAllowed = useHasMaskedJobCalls()
    useEnsureJobColors()

    const [, setSearchParams] = useSearchParams()
    const location = useLocation()
    const [settingsOpen, settingsOpenSet] = useState<boolean>(false)

    // React Router v6.1 setSearchParams has no functional updater, so read the live address
    // each time to stay fresh across quick successive writes.
    const writeParams = (values: Record<string, string | undefined>) => {
        const next = new URLSearchParams(window.location.search)
        Object.keys(values).forEach(k => {
            if (values[k]) next.set(k, values[k]!)
            else next.delete(k)
        })
        if (next.toString() !== new URLSearchParams(window.location.search).toString())
            setSearchParams(next, { replace: true })
    }


    // ---- Find (seeded from the address: ?find= survives switching view and coming back) ----

    const [keyword, keywordSet] = useState<string>(() => new URLSearchParams(window.location.search).get("find") ?? "")
    const findInputRef = useRef<HTMLInputElement>(null)
    useEffect(() => {
        const k = keyword.trim()
        props.onKeywordChange?.(k ? k : undefined)
        writeParams({ find: k ? keyword : undefined })
    }, [keyword])


    // ---- Group by / View by (seeded from the address) ----

    const [groupByItem, groupByItemSet] = useState<DataItem>(() =>
        GROUP_BY_OPTIONS.find(o => o.id === new URLSearchParams(window.location.search).get("groupBy")) ?? GROUP_BY_OPTIONS[0])
    useEffect(() => {
        if (props.mode !== "list") return
        props.onGroupByChange?.(groupByItem.id ?? "status")
        writeParams({ groupBy: groupByItem.id && groupByItem.id !== "status" ? groupByItem.id : undefined })
    }, [groupByItem])

    const [viewByItem, viewByItemSet] = useState<{ id: CalendarDateBasis; name: string; icon: string }>(() =>
        VIEW_BY_OPTIONS.find(o => o.id === new URLSearchParams(window.location.search).get("viewBy")) ?? VIEW_BY_OPTIONS[0])
    useEffect(() => {
        if (props.mode !== "calendar") return
        props.onViewByChange?.(viewByItem.id)
        writeParams({ viewBy: viewByItem.id !== "appointment" ? viewByItem.id : undefined })
    }, [viewByItem])

    const [groupMenuOpen, groupMenuOpenSet] = useState<boolean>(false)
    const [groupMenuPos, groupMenuPosSet] = useState<{ top: number, left: number }>()
    const groupBtnRef = useRef<HTMLAnchorElement>(null)
    const groupMenuRef = useRef<HTMLDivElement>(null)
    usePopupClose(groupMenuOpen, () => groupMenuOpenSet(false), [groupBtnRef, groupMenuRef], true)

    // View menu (list / grid / calendar / map): same chip + menu as Group.
    const [viewMenuOpen, viewMenuOpenSet] = useState<boolean>(false)
    const [viewMenuPos, viewMenuPosSet] = useState<{ top: number, left: number }>()
    const viewBtnRef = useRef<HTMLAnchorElement>(null)
    const viewMenuRef = useRef<HTMLDivElement>(null)
    usePopupClose(viewMenuOpen, () => viewMenuOpenSet(false), [viewBtnRef, viewMenuRef], true)
    useEffect(() => {
        if (viewMenuOpen) {
            const r = viewBtnRef.current?.getBoundingClientRect()
            if (r) viewMenuPosSet({ top: r.bottom + 6, left: r.left })
        }
    }, [viewMenuOpen])

    // View by menu (calendar only): same chip + menu as Group.
    const [viewByMenuOpen, viewByMenuOpenSet] = useState<boolean>(false)
    const [viewByMenuPos, viewByMenuPosSet] = useState<{ top: number, left: number }>()
    const viewByBtnRef = useRef<HTMLAnchorElement>(null)
    const viewByMenuRef = useRef<HTMLDivElement>(null)
    usePopupClose(viewByMenuOpen, () => viewByMenuOpenSet(false), [viewByBtnRef, viewByMenuRef], true)
    useEffect(() => {
        if (viewByMenuOpen) {
            const r = viewByBtnRef.current?.getBoundingClientRect()
            if (r) viewByMenuPosSet({ top: r.bottom + 6, left: r.left })
        }
    }, [viewByMenuOpen])


    // ---- Filters ----

    // Applied filters, seeded from the address; the panel edits a draft until Apply.
    const [applied, appliedSet] = useState<JobFilterValues>(() => readJobFilters(window.location.search))
    const [draft, draftSet] = useState<JobFilterValues>({})

    const ctx = useMemo<JobFilterContext>(() => ({
        maskedAllowed,
        statusOrder: sortJobStatuses(statuses ?? []).map(s => s.id ?? ""),
        jobColors,
        metros,
    }), [maskedAllowed, statuses, jobColors, metros])

    useEffect(() => {
        props.onFilterChange?.(buildJobFilter(applied, ctx))
        writeParams(jobFilterParams(applied))
    }, [applied, ctx])

    // Every filter's options, counted over all active jobs.
    const optionsByKey = useMemo(() => {
        const res: Record<string, JobFilterOption[]> = {}
        JOB_FILTERS.forEach(f => res[f.key] = f.options(jobsBank ?? [], ctx))
        return res
    }, [jobsBank, ctx])

    // What the panel lists: faceted by the draft. Each filter's options (and their counts) come
    // from the jobs that match every OTHER picked filter -- pick "Car Keys" and Technician lists
    // only techs with Car Keys jobs, counting those. A filter ignores its own picks so its other
    // values stay listed. Picked values that drop to zero stay in the list (count 0) so they can
    // still be seen and unpicked.
    const facetOptionsByKey = useMemo(() => {
        const res: Record<string, JobFilterOption[]> = {}
        JOB_FILTERS.forEach(f => {
            const others = { ...draft }
            delete others[f.key]
            delete others[rangeKey(f.key)]
            const match = buildJobFilter(others, ctx)
            const jobs = match ? (jobsBank ?? []).filter(match) : (jobsBank ?? [])
            const options = f.options(jobs, ctx)
            const picked = draft[f.key] ?? []
            picked.forEach(id => {
                if (options.some(o => o.id == id)) return
                const known = optionsByKey[f.key]?.find(o => o.id == id)
                if (known) options.push({ ...known, count: 0 })
            })
            res[f.key] = options
        })
        return res
    }, [draft, jobsBank, ctx, optionsByKey])

    const [filterOpen, filterOpenSet] = useState<boolean>(false)
    const [filterPos, filterPosSet] = useState<{ top: number, left: number, width: number }>()
    const filterBtnRef = useRef<HTMLAnchorElement>(null)
    const filterPanelRef = useRef<HTMLDivElement>(null)
    // No close-on-scroll here: the panel's own Select lists scroll.
    usePopupClose(filterOpen, () => filterOpenSet(false), [filterBtnRef, filterPanelRef], false)

    useEffect(() => {
        if (groupMenuOpen) {
            const r = groupBtnRef.current?.getBoundingClientRect()
            if (r) groupMenuPosSet({ top: r.bottom + 6, left: r.left })
        }
    }, [groupMenuOpen])

    useEffect(() => {
        if (filterOpen) {
            const r = filterBtnRef.current?.getBoundingClientRect()
            if (r) {
                // Laptop-size screens get the whole width (16px gutters); only very wide
                // monitors cap it, where a full-width panel would stretch the fields too far.
                const width = window.innerWidth < 1700 ? window.innerWidth - 32 : 1400
                filterPosSet({ top: r.bottom + 8, width, left: Math.min(Math.max(r.left, 16), window.innerWidth - width - 16) })
            }
        }
    }, [filterOpen])


    // ---- derived (after every hook: the project's ESLint flags consts between hooks) ----

    const appliedDefs = JOB_FILTERS.filter(f => (applied[f.key]?.length ?? 0) > 0)
    const appliedCount = appliedDefs.length
    const draftCount = countJobFilters(draft)

    const openFilters = () => {
        draftSet({ ...applied })
        filterOpenSet(true)
    }

    // Applied values' names; a custom range reads as its dates.
    const namesOf = (f: JobFilterDef, ids: string[]) =>
        ids.map(id => {
            if (id == CUSTOM) { const r = readRange(applied, f.key); return r ? formatRange(r) : undefined }
            return optionsByKey[f.key]?.find(o => o.id == id)?.name
        }).filter(Boolean) as string[]

    const setDraftRange = (key: string, from?: number, to?: number) =>
        draftSet(d => {
            const next = { ...d }
            const v = writeRange({ from, to })
            if (v) next[rangeKey(key)] = v
            else delete next[rangeKey(key)]
            return next
        })

    const optionRow = (o: JobFilterOption) => (
        <FlexRow className="w-100">
            {
                o.person
                    ? <Avatar avatarUrl={o.avatarUrl} fullName={o.name} width={20} className="me-2 flex-shrink-0" />
                    : o.color
                        ? <Icon icon="circle" type="solid" size={9} color={o.color} className="me-2" />
                        : o.icon && <Icon icon={o.icon} type="solid" size={11} className="me-2 text-gray-500" />
            }
            <span className="limited-text">{o.name}</span>
            <span className="ms-auto ps-2 fs-8 text-muted">{o.count}</span>
        </FlexRow>
    )

    const filterField = (f: JobFilterDef) => (
        <div key={`filter-field-${f.key}`} className="board-filter-field">
            <div className="board-filter-field-label">
                <Icon icon={f.icon} type="solid" size={10} className="me-2" />
                {f.title}
            </div>
            {/* labelUp (with no label) = one full-width column and no error row under it. */}
            {
                // Date filters are single-select: one period, or one custom range. Picking closes
                // the list so the range picker under it is in view.
                f.dateOf
                    ? <Select id={`sel-board-filter-${f.key}`} small solid labelUp
                        placeholder="Any" marginBottom={0}
                        source={facetOptionsByKey[f.key]} dataText="name" dataValue="id"
                        value={draft[f.key]?.[0] ?? ""}
                        optionTemplate={optionRow}
                        onSelect={v => draftSet(d => {
                            const next = { ...d }
                            if (v) next[f.key] = [v]
                            else delete next[f.key]
                            if (v != CUSTOM) delete next[rangeKey(f.key)]
                            return next
                        })} />
                    : <Select id={`sel-board-filter-${f.key}`} multiple small solid searchable labelUp
                        placeholder="Any" marginBottom={0}
                        source={facetOptionsByKey[f.key]} dataText="name" dataValue="id"
                        values={draft[f.key] ?? []}
                        optionTemplate={optionRow}
                        onChange={values => draftSet(d => ({ ...d, [f.key]: values }))} />
            }
            {
                // "Custom range" picked: a from/to date-time picker under the select.
                f.dateOf && draft[f.key]?.includes(CUSTOM) &&
                <div className="board-filter-range">
                    <DatePicker id={`dp-board-filter-${f.key}`} dateType="range" timePicker small solid labelUp
                        icon="calendar-range" placeholder="From - To" marginBottom={0} drops="auto"
                        selectedDate={(() => {
                            const r = readRange(draft, f.key)
                            return r?.from && r?.to ? `${moment.utc(r.from).format("DD/MM/YYYY HH:mm")} - ${moment.utc(r.to).format("DD/MM/YYYY HH:mm")}` : undefined
                        })()}
                        onPick={e => {
                            // The picked date-time read as UTC (see jobFilters). An end left at 00:00 means the
                            // whole end day; otherwise the end minute is included.
                            const utcOf = (m: any) => moment.utc(m.format("YYYY-MM-DD HH:mm"), "YYYY-MM-DD HH:mm")
                            const to = utcOf(e.endDate)
                            const end = to.hours() == 0 && to.minutes() == 0 ? to.endOf("day") : to.endOf("minute")
                            setDraftRange(f.key, utcOf(e.date).valueOf(), end.valueOf())
                        }}
                        onClear={() => setDraftRange(f.key)} />
                    {
                        !readRange(draft, f.key) &&
                        <div className="board-filter-range-hint">Pick a from and to date and time</div>
                    }
                </div>
            }
        </div>
    )


    const currentView = BOARD_VIEW_MODES.find(m => location.pathname.toLowerCase() === m.link.toLowerCase()) ?? BOARD_VIEW_MODES[0]
    const viewName = (m: ViewMode) => m.title.replace(/ View$/, "")

    return (
        <FlexRow className="board-filterbar">

            {
                // View: the board's views (list / grid / calendar / map) in a menu like Group's. The
                // links keep the address's params, so grouping, filters and Find carry over.
                <div className="me-2">
                    <a ref={viewBtnRef} href="#" className={`board-chip-btn${viewMenuOpen ? " open" : ""}`}
                        title="Switch the board's view"
                        onClick={e => {
                            e.preventDefault()
                            viewMenuOpenSet(o => !o)
                        }}>
                        <Icon icon={currentView.icon} size={11} type="solid" className="text-primary" />
                        <span className="board-chip-value">{viewName(currentView)}</span>
                        <Icon icon={viewMenuOpen ? "chevron-up" : "chevron-down"} size={9} className="board-chip-caret" />
                    </a>
                    {
                        viewMenuOpen && viewMenuPos && createPortal(
                            <div ref={viewMenuRef} className="board-groupby-menu" role="menu"
                                style={{ top: viewMenuPos.top, left: viewMenuPos.left }}>
                                <div className="board-groupby-menu-title">View</div>
                                {
                                    BOARD_VIEW_MODES.map(m => {
                                        const active = m.id === currentView.id
                                        return (
                                            <Link key={`board-view-${m.id}`} to={{ pathname: m.link, search: location.search }}
                                                role="menuitemradio" aria-checked={active}
                                                className={`board-groupby-item${active ? " active" : ""}`}
                                                onClick={() => viewMenuOpenSet(false)}>
                                                <Icon icon={m.icon} size={12} type="solid" className="me-3" />
                                                {viewName(m)}
                                                {active && <Icon icon="check" size={11} className="ms-auto" />}
                                            </Link>
                                        )
                                    })
                                }
                                {
                                    // Customize Board: settings of the view the person is in. Only
                                    // views that have any get it (list and grid, so far).
                                    ["/boards/jobs", "/boards/grid"].includes(location.pathname.toLowerCase()) &&
                                    <>
                                        <div className="board-groupby-menu-sep" />
                                        <a href="#" role="menuitem" className="board-groupby-item"
                                            onClick={e => {
                                                e.preventDefault()
                                                viewMenuOpenSet(false)
                                                settingsOpenSet(true)
                                            }}>
                                            <Icon icon="sliders" size={12} className="me-3" />
                                            Customize Board...
                                        </a>
                                    </>
                                }
                            </div>,
                            document.body)
                    }
                </div>
            }

            {
                // Group by: one compact chip (label + current choice); the options open in a menu.
                props.mode === "list" &&
                <div className="me-2">
                    <a ref={groupBtnRef} href="#" className={`board-chip-btn${groupMenuOpen ? " open" : ""}`}
                        title="Group the board by"
                        onClick={e => {
                            e.preventDefault()
                            groupMenuOpenSet(o => !o)
                        }}>
                        <Icon icon={groupByItem.icon as any} size={11} type="solid" className="text-primary" />
                        <span className="board-chip-value">{groupByItem.name}</span>
                        <Icon icon={groupMenuOpen ? "chevron-up" : "chevron-down"} size={9} className="board-chip-caret" />
                    </a>
                    {
                        groupMenuOpen && groupMenuPos && createPortal(
                            <div ref={groupMenuRef} className="board-groupby-menu" role="menu"
                                style={{ top: groupMenuPos.top, left: groupMenuPos.left }}>
                                <div className="board-groupby-menu-title">Group by</div>
                                {
                                    GROUP_BY_OPTIONS.map(o => {
                                        const active = o.id === groupByItem.id
                                        return (
                                            <a key={`groupby-${o.id}`} href="#" role="menuitemradio" aria-checked={active}
                                                className={`board-groupby-item${active ? " active" : ""}`}
                                                onClick={e => {
                                                    e.preventDefault()
                                                    groupMenuOpenSet(false)
                                                    if (!active) groupByItemSet(o)
                                                }}>
                                                <Icon icon={o.icon as any} size={12} type="solid" className="me-3" />
                                                {o.name}
                                                {active && <Icon icon="check" size={11} className="ms-auto" />}
                                            </a>
                                        )
                                    })
                                }
                            </div>,
                            document.body)
                    }
                </div>
            }

            {
                // View by (calendar): which date places a job on the calendar; same chip + menu as Group.
                props.mode === "calendar" &&
                <div className="me-2">
                    <a ref={viewByBtnRef} href="#" className={`board-chip-btn${viewByMenuOpen ? " open" : ""}`}
                        title="Place jobs on the calendar by"
                        onClick={e => {
                            e.preventDefault()
                            viewByMenuOpenSet(o => !o)
                        }}>
                        <Icon icon={viewByItem.icon as any} size={11} type="solid" className="text-primary" />
                        <span className="board-chip-value">{viewByItem.name}</span>
                        <Icon icon={viewByMenuOpen ? "chevron-up" : "chevron-down"} size={9} className="board-chip-caret" />
                    </a>
                    {
                        viewByMenuOpen && viewByMenuPos && createPortal(
                            <div ref={viewByMenuRef} className="board-groupby-menu" role="menu"
                                style={{ top: viewByMenuPos.top, left: viewByMenuPos.left }}>
                                <div className="board-groupby-menu-title">View by</div>
                                {
                                    VIEW_BY_OPTIONS.map(o => {
                                        const active = o.id === viewByItem.id
                                        return (
                                            <a key={`viewby-${o.id}`} href="#" role="menuitemradio" aria-checked={active}
                                                className={`board-groupby-item${active ? " active" : ""}`}
                                                onClick={e => {
                                                    e.preventDefault()
                                                    viewByMenuOpenSet(false)
                                                    if (!active) viewByItemSet(o)
                                                }}>
                                                <Icon icon={o.icon as any} size={12} type="solid" className="me-3" />
                                                {o.name}
                                                {active && <Icon icon="check" size={11} className="ms-auto" />}
                                            </a>
                                        )
                                    })
                                }
                            </div>,
                            document.body)
                    }
                </div>
            }

            {/* Filter: the same chip style as Group; opens the big filter panel. */}
            <a ref={filterBtnRef} href="#" className={`board-chip-btn${appliedCount > 0 ? " board-chip-filtered me-1" : " me-2"}${filterOpen ? " open" : ""}`}
                title={appliedCount > 0
                    ? appliedDefs.map(f => `${f.title}: ${namesOf(f, applied[f.key]).join(", ")}`).join("\n")
                    : "Filter the board"}
                onClick={e => {
                    e.preventDefault()
                    if (filterOpen) filterOpenSet(false)
                    else openFilters()
                }}>
                <Icon icon="filter" size={12} type="solid" className="board-chip-lead" />
                {
                    // The applied filters sit inside the chip as small tags: "Status: Pending +1".
                    // Two shown, the rest as "+N"; the full list is in the chip's tooltip.
                    // Laptop-size screens have no room for tags: they get just a count (CSS swaps them).
                    appliedCount > 0 &&
                    <span className="board-chip-value board-chip-count">
                        {appliedCount} {appliedCount == 1 ? "filter" : "filters"}
                    </span>
                }
                {
                    appliedCount > 0
                        ? <span className="board-chip-tags">
                            {
                                appliedDefs.slice(0, 2).map(f => {
                                    const names = namesOf(f, applied[f.key])
                                    const shown = names.length > 0 ? names : [`${applied[f.key].length} selected`]
                                    return (
                                        <span key={`filter-tag-${f.key}`} className="board-chip-tag">
                                            <Icon icon={f.icon} size={9} type="solid" className="me-1" />
                                            <span className="board-chip-tag-key">{f.title}:</span>
                                            <span className="board-chip-tag-val">{shown[0]}</span>
                                            {shown.length > 1 && <span className="board-chip-tag-more">+{shown.length - 1}</span>}
                                        </span>
                                    )
                                })
                            }
                            {appliedCount > 2 && <span className="board-chip-tag board-chip-tag-rest">+{appliedCount - 2}</span>}
                        </span>
                        : <span className="board-chip-value board-chip-none">None</span>
                }
                <Icon icon={filterOpen ? "chevron-up" : "chevron-down"} size={9} className="board-chip-caret" />
            </a>

            {
                appliedCount > 0 &&
                <a href="#" className="board-chip-clear me-2" title="Clear all filters" onClick={e => {
                    e.preventDefault()
                    appliedSet({})
                }}>
                    <Icon icon="xmark" size={12} />
                </a>
            }

            {
                // Find: same chip as Group / Filter, with a see-through text box as its value.
                // Typing filters the board at once, across every field of the job.
                <label className={`board-chip-btn board-find-chip ms-2${keyword.trim() ? " board-chip-filtered" : ""}`}
                    title="Find in every field of the job">
                    <Icon icon="magnifying-glass" size={12} type="solid" className="board-chip-lead" />
                    <input ref={findInputRef} type="text" className="board-find-input" placeholder="Name, phone, address, job id..."
                        value={keyword}
                        onChange={e => keywordSet(e.target.value)}
                        onKeyDown={e => { if (e.key == "Escape") keywordSet("") }} />
                    {
                        keyword.length > 0 &&
                        <a href="#" className="board-find-clear" title="Clear" onClick={e => {
                            e.preventDefault()
                            keywordSet("")
                            findInputRef.current?.focus()
                        }}>
                            <Icon icon="xmark" size={11} />
                        </a>
                    }
                </label>
            }

            {
                settingsOpen &&
                <JobsBoardSettingsDrawer onClose={() => settingsOpenSet(false)} />
            }

            {
                filterOpen && filterPos && createPortal(
                    <div ref={filterPanelRef} className="board-filter-panel"
                        style={{ top: filterPos.top, left: filterPos.left, width: filterPos.width }}>

                        <div className="board-filter-cols">
                            {
                                PANEL_COLUMNS.map((sections, ci) =>
                                    <div key={`filter-col-${ci}`} className="board-filter-col">
                                        {
                                            sections.map(section =>
                                                <div key={`filter-sec-${section}`} className="board-filter-section">
                                                    <div className="board-filter-section-title">{section}</div>
                                                    {JOB_FILTERS.filter(f => f.section == section).map(filterField)}
                                                </div>
                                            )
                                        }
                                    </div>
                                )
                            }
                        </div>

                        <FlexRow className="board-filter-foot">
                            <a href="#" className={`board-filter-reset${draftCount == 0 ? " disabled" : ""}`} onClick={e => {
                                e.preventDefault()
                                draftSet({})
                            }}>
                                <Icon icon="rotate-left" size={11} className="me-1" />
                                Reset all
                            </a>
                            <span className="ms-auto me-3 fs-8 text-muted">
                                {draftCount == 0 ? "No filters" : `${draftCount} filter${draftCount > 1 ? "s" : ""}`}
                                {" · values inside a filter match any, filters combine"}
                            </span>
                            <button type="button" className="btn btn-sm btn-light me-2" onClick={() => filterOpenSet(false)}>
                                Cancel
                            </button>
                            <button type="button" className="btn btn-sm btn-primary" onClick={() => {
                                appliedSet(cleanJobFilters(draft))
                                filterOpenSet(false)
                            }}>
                                Apply
                            </button>
                        </FlexRow>
                    </div>,
                    document.body)
            }

        </FlexRow>
    )
}
